import React from 'react';
import { createStyles, makeStyles, Theme, Typography } from '@material-ui/core';
import { Switch } from 'components/UI/V2/Forms/Switch/Switch';
import { useMarketplace, useTimeZone } from 'hooks';
import { BusinessSettingsTitle } from 'components/Marketplace/BusinessSettings/UI';
import { CustomQuestion } from 'components/Marketplace/BusinessSettings/IntakeForm/CustomQuestions/CustomQuestion';
import Button from 'components/UI/V2/Button/Button';
import { faPlus, faTimesCircle } from '@fortawesome/pro-solid-svg-icons';
import { IntakeFormCustomQuestion, IntakeFormCustomQuestionCategory } from '@spike/marketplace-model';
import moment from 'moment-timezone';
import { v4 as uuid } from 'uuid';
import { useSelector } from 'react-redux';
import { RootState } from 'store';
import { MarketplaceStatus } from '@spike/marketplace-action';
import { AlertMessage2 } from 'components/AlertMessage2';

interface CustomQuestionsProps {
    onEnable: (enabled: boolean) => void;
    onSave: (enabled: boolean, questions: IntakeFormCustomQuestion[], deletedIds: number[]) => void;
}

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        root: {
            paddingBottom: 32,

            [theme.breakpoints.up('sm')]: {
                marginTop: 32
            }
        },
        header: {
            'display': 'flex',
            'alignItems': 'center',
            'justifyContent': 'space-between',

            '& div': {
                margin: 0
            }
        },
        description: {
            fontSize: 16,
            lineHeight: 1.4,
            marginTop: 8,

            [theme.breakpoints.down('sm')]: {
                fontSize: 14
            }
        },
        alert: {
            'marginTop': 32,
            'borderWidth': '2px !important',

            '& p': {
                fontSize: 14,
                fontWeight: 500
            }
        },
        questionsList: {
            gap: 24,
            marginTop: 32,
            display: 'flex',
            flexDirection: 'column'
        },
        footer: {
            'gap': 32,
            'marginTop': 32,
            'display': 'flex',
            'flexDirection': 'column',

            '& button:first-child': {
                alignSelf: 'flex-start'
            },

            [theme.breakpoints.up('md')]: {
                flexDirection: 'row',
                alignItems: 'center',
                justifyContent: 'space-between'
            }
        }
    })
);

export const CustomQuestions: React.FC<CustomQuestionsProps> = props => {
    const classes = useStyles();
    const timeZone = useTimeZone();
    const marketplace = useMarketplace();

    const now = moment().tz(timeZone);

    const marketplaceStatus = useSelector<RootState, MarketplaceStatus>(state => state.marketplace.status);

    const isEnabled = React.useMemo(
        () => marketplace.intakeForm.questionsEnabled,
        [marketplace.intakeForm.questionsEnabled]
    );

    const [isLoading, setIsLoading] = React.useState(false);
    const [alert, setAlert] = React.useState<string | null>(null);
    const [errors, setErrors] = React.useState<Record<string, string>>({});
    const [deletedQuestionIds, setDeletedQuestionIds] = React.useState<Array<number>>([]);
    const [questions, setQuestions] = React.useState(marketplace.intakeForm.questions);

    React.useEffect(() => {
        setQuestions(marketplace.intakeForm.questions);
    }, [marketplace.intakeForm.questions]);

    React.useEffect(() => {
        if (!marketplace.intakeForm.questionsEnabled) {
            setAlert('');
        }
    }, [marketplace.intakeForm.questionsEnabled]);

    React.useEffect(() => {
        if (marketplaceStatus === MarketplaceStatus.SaveIntakeFormQuestionsSuccess) {
            setIsLoading(false);
            setDeletedQuestionIds([]);
        }
    }, [marketplaceStatus]);

    const validateQuestions = () => {
        const newErrors = questions.reduce((acc, curr) => {
            if (curr.question.length === 0) {
                acc[curr.uuid] = 'Question is required';
            }

            if (curr.question.length > 255) {
                acc[curr.uuid] = 'Question must be less than 255 characters';
            }

            return acc;
        }, {} as Record<string, string>);

        setErrors(newErrors);

        return Object.keys(newErrors).length === 0;
    };

    const toggleQuestionsHandler = () => {
        if (isLoading) {
            return;
        }

        setIsLoading(true);
        props.onEnable(!isEnabled);
    };

    const addQuestionHandler = () => {
        setAlert(null);
        setQuestions([
            ...questions.map((q, index) => ({ ...q, order: index + 1 })),
            {
                uuid: uuid(),
                question: '',
                enabled: true,
                required: true,
                order: questions.length + 1,
                marketplaceId: Number(marketplace.id),
                category: IntakeFormCustomQuestionCategory.FOR_CUSTOMERS,
                updatedAt: now,
                createdAt: now
            }
        ]);
    };

    const deleteQuestionHandler = (uuid: string) => {
        const questionToDelete = questions.find(q => q.uuid === uuid);

        if (!questionToDelete) {
            return;
        }

        if (questionToDelete.id && questionToDelete.id !== 0) {
            setDeletedQuestionIds([...deletedQuestionIds, Number(questionToDelete.id)]);
        }

        setQuestions(questions.filter(q => q.uuid !== uuid));
    };

    const questionChangedHandler = (question: IntakeFormCustomQuestion) => {
        const questionIsInvalid = question.question.length === 0 || question.question.length > 255;

        if (question.question.length === 0) {
            setErrors({ ...errors, [question.uuid]: 'Question is required' });
        }

        if (question.question.length > 255) {
            setErrors({ ...errors, [question.uuid]: 'Question must be less than 255 characters' });
        }

        if (!questionIsInvalid) {
            setErrors(Object.fromEntries(Object.entries(errors).filter(([key]) => key !== question.uuid)));
        }

        setQuestions(questions.map(q => (q.uuid === question.uuid ? question : q)));
    };

    const saveQuestionsHandler = () => {
        if (isLoading) {
            return;
        }

        const areQuestionsValid = validateQuestions();

        if (!areQuestionsValid) {
            return;
        }

        const isIntakeFormEmpty = questions.length === 0;

        setIsLoading(true);
        props.onSave(isIntakeFormEmpty ? false : isEnabled, questions, deletedQuestionIds);
    };

    return (
        <div className={classes.root}>
            <div className={classes.header}>
                <BusinessSettingsTitle>Custom Questions</BusinessSettingsTitle>

                <Switch
                    checked={isEnabled}
                    onChange={toggleQuestionsHandler}
                />
            </div>

            <Typography className={classes.description}>
                Create personalized questions to gather the information you need from clients & pets.
            </Typography>

            {alert && (
                <AlertMessage2
                    icon={faTimesCircle}
                    bdColor="#EF4F57"
                    bgColor="rgba(251, 185, 188, 0.20)"
                    className={classes.alert}
                >
                    <Typography>{alert}</Typography>
                </AlertMessage2>
            )}

            {isEnabled && (
                <>
                    {questions.length > 0 && (
                        <div className={classes.questionsList}>
                            {questions.map((question, index) => (
                                <CustomQuestion
                                    key={index}
                                    question={question}
                                    error={errors[question.uuid]}
                                    onChange={questionChangedHandler}
                                    onDelete={deleteQuestionHandler}
                                />
                            ))}
                        </div>
                    )}

                    <div className={classes.footer}>
                        <Button
                            size="inline"
                            variant="text-green"
                            label="Add Question"
                            leftIcon={faPlus}
                            onClick={addQuestionHandler}
                        />

                        <Button
                            label="Save"
                            size="large"
                            variant="primary"
                            isLoading={isLoading}
                            onClick={saveQuestionsHandler}
                        />
                    </div>
                </>
            )}
        </div>
    );
};
